import { yupResolver } from "@hookform/resolvers/yup"
import { useForm } from "react-hook-form"
import * as yup from "yup"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { useState, useEffect } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import ModalHeader from "../../../../Components/ModalHeader/ModalHeader"
import ModalScrollbar from "../../../../Components/ModalScrollbar/ModalScrollbar"
import GridContainer from "../../../../Components/TextField/GridContainer"
import BasicTextField from "../../../../Components/TextField/BasicTextField"
import { ModalFooter } from "../../../../Constants/StyledComponents"
import TableWrapper from "../../../../Components/TextField/TableWrapper"
import ModernMUITable from "../../../../Components/ModernMUITable"
import { useEndpointProvider } from "../../../../Providers/EndpointProvider"
import { useTableContext } from "../../../../Providers/TableProvider"
import { useModalProvider } from "../../../../Providers/ModalProvider"
import { MobergButton, MobergButtonShape, MobergButtonVariant } from "../../../../Components/MobergButton/MobergButton"
import { MobergTheme } from "../../../../Components/MobergThemes/MobergColors"
import { MobergFontSize } from "../../../../Components/MobergFont/MobergFont"
import { useBackendLinksProvider } from "../../../../Providers/BackendLinksProvider"

const userColumns = [
	{ field: "email", flex: 1, headerName: "Email", visible: true },
	{ field: "first_name", flex: 0.5, headerName: "First name", visible: true },
	{ field: "last_name", flex: 0.5, headerName: "Last name", visible: true },
	{
		field: "is_verified",
		flex: 0.3,
		headerName: "Verified",
		renderCell: params => {
			return params.value ? <CheckCircleIcon color="success" /> : null
		},
		headerAlign: "center",
		align: "center",
		visible: true
	},
]

export default function CreateProjectModal() {
	const endpointProvider = useEndpointProvider()
	const { setToastProps } = useTableContext()
	const queryClient = useQueryClient()
	const { close } = useModalProvider()
	const { LINKS } = useBackendLinksProvider()

	const [selectedRows, setSelectedRows] = useState([])
	const [errorMessage, setErrorMessage] = useState()

	const modalTableToolbarProps = {
		searchInput: { isShow: true },
		columnButton: { isShow: false }
	}

	const queryProps = {
		queryKey: "users",
		endpoint: LINKS.ADMIN.USER_MANAGEMENT.USERS.GET_USERS,
	}

	const schema = yup
		.object({
			name: yup.string().required("Name is required."),
			identifier: yup.string().required("Project ID is required."),
		})
		.required()

	const { control, handleSubmit, formState: { errors } } = useForm({
		resolver: yupResolver(schema),
		defaultValues: { name: "", identifier: "" },
	})

	const errorCodeHandlerMap = new Map()
	errorCodeHandlerMap.set(409, json => setErrorMessage(json.errorMessage))

	const createProject = async body => endpointProvider.post(LINKS.ADMIN.PROJECTS.CREATE_PROJECT, body, errorCodeHandlerMap)
	const createProjectMutation = useMutation({
		mutationFn: body => createProject(body),
		onSuccess: () => {
			setToastProps({ isOpen: true, severity: "success", message: "Project created successfully." })
			close()
		},
		onSettled: () => {
			queryClient.invalidateQueries("projects")
		},
	})

	const onSubmit = data => {
		if (selectedRows.length === 0) {
			setErrorMessage("Please select at least one user.")
			return
		}
		createProjectMutation.mutate({
			...data,
			user_ids: selectedRows,
		})
	}

	useEffect(() => {
		if (Object.keys(errors).length > 0) {
			const error = errors[Object.keys(errors)[0]]
			if (Array.isArray(error)) {
				error.forEach(e => {
					if (e) {
						setErrorMessage(e?.message)
					}
				})
			} else {
				setErrorMessage(error?.message)
			}
		}
	}, [errors])

	return (
		<>
			<ModalHeader headerText="Create project" />
			<ModalScrollbar>
				<div style={{ maxWidth: "800px" }}>
					<h2>Project Details</h2>
					<GridContainer>
						<BasicTextField
							inputId="create-project-name-id"
							label="Name"
							tooltip="The name of the project."
							placeholder="Project Name"
							autoFocus={true}
							control={control}
							name="name"
							numberColumn={6}
						/>
						<BasicTextField
							inputId="create-project-id-id"
							label="Project ID"
							tooltip="The id of the project."
							placeholder="Project ID"
							control={control}
							name="identifier"
							numberColumn={6}
						/>
						<TableWrapper inputId="create-project-users-id" label="Users">
							<ModernMUITable 
								columns={userColumns} 
								{...queryProps} 
								tableToolbarProps={modalTableToolbarProps}
								enableRowSelection={true}
								checkboxSelection={true}
								onSelectionChange={setSelectedRows}
								rowSelectionModel={{ type: 'include', ids: new Set(selectedRows) }}
								isRowSelectable={() => true}
								height="300px"
							/>
						</TableWrapper>
					</GridContainer>
				</div>
			</ModalScrollbar>
			<ModalFooter style={{ justifyContent: "space-between" }}>
				<h1 style={{ color: "#E54E58" }}>{errorMessage ? errorMessage : null}</h1>
				<MobergButton
					theme={MobergTheme.BLUE}
					variant={MobergButtonVariant.FILLED}
					fontSize={MobergFontSize.LARGE}
					shape={MobergButtonShape.WIDE}
					onClick={handleSubmit(onSubmit)}>
					Submit
				</MobergButton>
			</ModalFooter>
		</>
	)
}